import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, 
  Search, 
  TrendingUp, 
  Sun,
  Moon,
  Menu,
  X,
  Settings,
  Eye,
  Users,
  GraduationCap,
  Crown,
  Lock,
  Mail,
  Package,
  Truck,
  LogOut,
  Shield,
  BarChart3,
  Calculator,
  Flame,
  Gift,
  Play,
  BookOpen,
  ShoppingBag,
} from "lucide-react";
import { useTheme } from "./ThemeProvider";
import { useSubscription } from "../context/SubscriptionContext";
import { useAuth } from "../context/AuthContext";
import { useState, useEffect } from "react";

import { SuiteNav } from "./ProfimySuite";

const navItems = [
  { path: "/spin", icon: Gift, label: "Roue Fortune", locked: false, golden: true },
  { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard", locked: false },
  { path: "/opportunities", icon: Flame, label: "Opportunités", locked: true },
  { path: "/scanner", icon: Search, label: "Scanner", locked: true },
  { path: "/comparateur", icon: BarChart3, label: "Comparateur", locked: true },
  { path: "/watchlist", icon: Eye, label: "Watchlist", locked: true },
  { path: "/produits-500", icon: Package, label: "500 Produits", locked: true },
  { path: "/fournisseurs-300", icon: Truck, label: "300 Fournisseurs", locked: true },
  { path: "/produits-marketplaces", icon: ShoppingBag, label: "Marketplaces", locked: false },
  { path: "/calculateur-douane", icon: Calculator, label: "Douane", locked: true },
  { path: "https://www.profimy-academie.com/", icon: GraduationCap, label: "Académie", external: true, locked: false },
  { path: "/notifications", icon: Settings, label: "Alertes", locked: true },
  { path: "/subscription", icon: Crown, label: "Abonnement", locked: false },
  { path: "/affiliation", icon: Users, label: "Affiliation", locked: false },
  { path: "https://youtu.be/aPyMctQJhbA", icon: Play, label: "Tuto YouTube", external: true, locked: false },
  { path: "/blog", icon: BookOpen, label: "Blog", locked: false },
  { path: "/contact", icon: Mail, label: "Contact", locked: false },
  { path: "/admin", icon: Shield, label: "Admin", locked: false, adminOnly: true },
];

export default function Layout() {
  const { theme, setTheme } = useTheme();
  const { isSubscribed } = useSubscription();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark");
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen">
      {/* Mobile menu button */}
      <button
        data-testid="mobile-menu-toggle"
        className="md:hidden fixed top-4 left-4 z-50 neo-button-secondary p-2"
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Sidebar */}
      <aside 
        className={`fixed md:sticky top-0 h-screen z-40 transition-transform duration-300 w-64 bg-white dark:bg-[#050505] border-r-2 border-[#0A0A0A] dark:border-[#00E676]/15 p-6 flex flex-col overflow-y-auto ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
      >
        <div className="mb-8">
          <NavLink to="/" className="block">
            <h1 className="text-2xl font-extrabold tracking-tight flex items-center gap-2">
              <span className="bg-[#00E676] text-[#0A0A0A] px-2 py-1 border-2 border-[#0A0A0A]">
                PROFIMY
              </span>
              <span className="dark:text-white">MARGIN</span>
            </h1>
          </NavLink>
          <p className="text-sm text-muted-foreground mt-1 uppercase tracking-wider">
            Sourcing Intelligent
          </p>
          <p className="text-[10px] text-muted-foreground mt-0.5 tracking-wide">
            profimy.app
          </p>
        </div>

        <nav className="flex-1 space-y-2">
          <SuiteNav current="profimy-app" home="/dashboard" onNavigate={() => setSidebarOpen(false)} />
          {navItems.filter(item => !item.adminOnly || (user && user.role === "admin")).map(({ path, icon: Icon, label, external, locked, golden }) => {
            const showLock = locked && !isSubscribed;
            
            return external ? (
              <a
                key={path}
                href={path}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}`}
                className="flex items-center gap-3 px-4 py-3 font-bold uppercase tracking-wide text-sm transition-all duration-200 border-2 border-transparent hover:border-[#0A0A0A] hover:bg-[#F1F5F9] dark:hover:bg-[#1A1A1A]"
              >
                <Icon size={20} />
                {label}
              </a>
            ) : (
              <NavLink
                key={path}
                to={path}
                data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}`}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 font-bold uppercase tracking-wide text-sm transition-all duration-200 border-2 ${
                    isActive
                      ? "bg-[#00E676] text-[#0A0A0A] border-[#0A0A0A] shadow-[4px_4px_0px_#0A0A0A]"
                      : golden
                      ? "border-[#FFD700] bg-gradient-to-r from-[#FFD700]/20 to-[#FF8C00]/20 text-[#B8860B] dark:text-[#FFD700] animate-pulse hover:animate-none hover:shadow-[0_0_20px_rgba(255,215,0,0.4)]"
                      : showLock
                      ? "border-transparent text-muted-foreground/60 hover:border-[#0A0A0A]/30"
                      : "border-transparent hover:border-[#0A0A0A] hover:bg-[#F1F5F9] dark:hover:bg-[#1A1A1A]"
                  }`
                }
              >
                <Icon size={20} className={golden ? "text-[#FFD700]" : ""} />
                <span className="flex-1">{label}</span>
                {showLock && <Lock size={14} className="text-muted-foreground/50" />}
              </NavLink>
            );
          })}
        </nav>

        <button
          data-testid="theme-toggle"
          onClick={toggleTheme}
          className="neo-button-secondary w-full flex items-center justify-center gap-2 mt-auto"
        >
          {theme === "dark" ? <Sun size={20} /> : <Moon size={20} />}
          <span>{theme === "dark" ? "Light Mode" : "Dark Mode"}</span>
        </button>

        {user && (
          <div className="mt-3 space-y-2">
            <div className="flex items-center gap-2 px-2 py-1.5 truncate">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-7 h-7 rounded-full border border-[#00E676]/50 flex-shrink-0" />
              ) : (
                <div className="w-7 h-7 bg-[#00E676]/10 border border-[#00E676]/30 flex items-center justify-center flex-shrink-0 text-[#00E676] font-bold text-xs">
                  {user.name?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase()}
                </div>
              )}
              <span className="text-xs font-bold truncate" data-testid="user-email">{user.email}</span>
            </div>
            <button
              onClick={handleLogout}
              className="neo-button-secondary w-full flex items-center justify-center gap-2 text-xs"
              data-testid="logout-btn"
            >
              <LogOut size={16} />
              <span>Déconnexion</span>
            </button>
          </div>
        )}
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 p-4 pt-16 md:p-8 lg:p-12 md:pt-8 lg:pt-12 overflow-auto min-w-0">
        <Outlet />
      </main>

      {/* Opportunities popup */}
      <OpportunitiesPopup />
      {/* Spin wheel popup */}
      <SpinWheelPopup />
    </div>
  );
}

function OpportunitiesPopup() {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Show popup after 12 seconds, only once per session
    const alreadySeen = sessionStorage.getItem("opp_popup_seen");
    if (alreadySeen) return;
    const timer = setTimeout(() => {
      setShow(true);
      sessionStorage.setItem("opp_popup_seen", "1");
    }, 12000);
    return () => clearTimeout(timer);
  }, []);

  if (!show || dismissed) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="opp-popup">
      <div className="neo-card p-6 sm:p-10 max-w-md w-[90%] text-center border-[#FF5722] shadow-[0_0_60px_rgba(255,87,34,0.3)] bg-background relative">
        <button
          onClick={() => setDismissed(true)}
          className="absolute top-3 right-3 text-muted-foreground hover:text-foreground p-1"
          data-testid="opp-popup-close"
        >
          <X size={20} />
        </button>
        <div className="w-16 h-16 bg-[#FF5722] border-2 border-[#0A0A0A] flex items-center justify-center mx-auto mb-5 shadow-[4px_4px_0px_#0A0A0A]">
          <Flame size={32} className="text-white" />
        </div>
        <h2 className="font-extrabold text-xl sm:text-2xl uppercase tracking-tight mb-2">
          Opportunités Grosse Marge
        </h2>
        <p className="text-muted-foreground text-sm mb-5">
          10 offres exclusives avec <strong>+30% de marge minimum</strong>, selectionnees par <strong>Joseph</strong>. Mise a jour journaliere.
        </p>
        <div className="flex items-baseline justify-center gap-1 mb-5">
          <span className="text-4xl font-extrabold text-[#00E676]">4,90€</span>
        </div>
        <button
          onClick={() => { setDismissed(true); navigate("/opportunities"); }}
          className="w-full bg-[#FF5722] text-white font-bold uppercase tracking-wider px-6 py-3 text-sm border-2 border-[#0A0A0A] shadow-[4px_4px_0px_#0A0A0A] hover:shadow-none transition-shadow"
          data-testid="opp-popup-cta"
        >
          Débloquer maintenant
        </button>
        <p className="text-xs text-muted-foreground mt-3">Paiement unique · Accès immédiat</p>
      </div>
    </div>
  );
}


function SpinWheelPopup() {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Show 25 seconds after opportunities popup (which fires at 12s)
    const alreadySeen = sessionStorage.getItem("spin_popup_seen");
    if (alreadySeen) return;
    const timer = setTimeout(() => {
      setShow(true);
      sessionStorage.setItem("spin_popup_seen", "1");
    }, 25000);
    return () => clearTimeout(timer);
  }, []);

  if (!show || dismissed) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="spin-popup">
      <div className="neo-card p-6 sm:p-10 max-w-md w-[90%] text-center border-[#FFD700] shadow-[0_0_60px_rgba(255,215,0,0.3)] bg-background relative">
        <button
          onClick={() => setDismissed(true)}
          className="absolute top-3 right-3 text-muted-foreground hover:text-foreground p-1"
          data-testid="spin-popup-close"
        >
          <X size={20} />
        </button>
        <div className="w-16 h-16 bg-[#FFD700] border-2 border-[#0A0A0A] flex items-center justify-center mx-auto mb-5 shadow-[4px_4px_0px_#0A0A0A] animate-bounce">
          <Gift size={32} className="text-[#0A0A0A]" />
        </div>
        <h2 className="font-extrabold text-xl sm:text-2xl uppercase tracking-tight mb-2">
          Roue de la Fortune
        </h2>
        <p className="text-muted-foreground text-sm mb-5">
          <strong>3 spins gratuits par jour !</strong> Gagnez des produits, fournisseurs, formations et abonnements.
        </p>
        <button
          onClick={() => { setDismissed(true); navigate("/spin"); }}
          className="w-full bg-[#FFD700] text-[#0A0A0A] font-bold uppercase tracking-wider px-6 py-3 text-sm border-2 border-[#0A0A0A] shadow-[4px_4px_0px_#0A0A0A] hover:shadow-none transition-shadow"
          data-testid="spin-popup-cta"
        >
          Tourner la roue
        </button>
      </div>
    </div>
  );
}
